package com.dotdrives.web.pages.drives

import com.dotdrives.design.DotTone
import com.dotdrives.model.DriveFinancials
import com.dotdrives.model.DriveSummary
import com.dotdrives.web.ui.DotAction
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import kotlinx.browser.window
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface CompletedDrivesListProps : Props {
    var financialTotals: com.dotdrives.model.DriveFinancialTotals?
    var loading: Boolean
    var drives: List<DriveSummary>
    var financials: Map<String, DriveFinancials>
    var page: Int
    var totalPages: Int
    var totalCount: Long
    var error: String?
    var onPage: (Int) -> Unit
    var onRetry: () -> Unit
    var onOpen: (DriveSummary) -> Unit
    var onBackToActive: () -> Unit
}

val CompletedDrivesList = FC<CompletedDrivesListProps> { props ->
    props.error?.let { error ->
        DotBanner {
            message = error
            onRetry = props.onRetry
            className = "drives-inline-banner"
        }
    }

    div {
        className = ClassName("drives-completed-card")
        div {
            className = ClassName("drives-completed-head")
            div {
                className = ClassName("drives-completed-title-row")
                div { className = ClassName("drives-completed-title"); +"Closed drives" }
                props.financialTotals?.let { totals ->
                    DotPill {
                        text = "${formatMoneyFull(totals.receivedAmount)} RECEIVED"
                        tone = DotTone.Success
                    }
                }
            }
            span {
                className = ClassName("drives-completed-meta")
                +"${props.totalCount} closed drives"
            }
        }
        when {
            props.loading && props.drives.isEmpty() -> div {
                className = ClassName("drives-completed-list")
                repeat(5) { DotSkeleton { height = "48px" } }
            }
            props.drives.isEmpty() && props.error == null -> div {
                className = ClassName("drives-completed-list")
                DotEmptyState {
                    emoji = "🎉"
                    title = "No closed drives in this period"
                    body = "Try a different date range or all time."
                    primaryAction = DotAction("Back to board", props.onBackToActive)
                    secondaryAction = DotAction("▶ Watch how drives work · 2 min") {
                        window.open("https://www.dotdrives.com", "_blank", "noopener,noreferrer")
                    }
                }
            }
            else -> div {
                className = ClassName("drives-completed-list")
                props.drives.forEach { drive ->
                    button {
                        key = drive.id.unsafeCast<Key>()
                        type = ButtonType.buttonType
                        className = ClassName("drives-completed-row")
                        onClick = { props.onOpen(drive) }
                        span { className = ClassName("drives-completed-icon"); +outcomeIcon(drive.outcome) }
                        div {
                            className = ClassName("drives-completed-copy")
                            div { className = ClassName("drives-completed-name"); +drive.title }
                            div {
                                className = ClassName("drives-completed-sub")
                                +"${drive.target ?: "No target"} · ${drive.closedAt?.toString()?.take(10) ?: "Date unavailable"} · ${if (drive.archivedAt != null) "Archived" else "Terminal phase entered"}"
                            }
                        }
                        DotPill {
                            text = if (drive.archivedAt != null) {
                                listOfNotNull("ARCHIVED", drive.outcome?.uppercase()).joinToString(" · ")
                            } else drive.outcome?.uppercase() ?: "CLOSED"
                            tone = outcomeTone(drive.outcome)
                        }
                        props.financials[drive.id]?.let {
                            span {
                                className = ClassName("drives-completed-amount")
                                +formatMoneyFull(completedRowAmount(drive, props.financials))
                            }
                        }
                    }
                }
                CompletedPagination {
                    currentPage = props.page
                    totalPages = props.totalPages
                    onPage = props.onPage
                }
            }
        }
    }
}

private fun outcomeIcon(outcome: String?): String = when (outcome) {
    "received" -> "🎉"
    "pledged" -> "🤝"
    "declined" -> "💤"
    else -> "•"
}

private fun outcomeTone(outcome: String?): DotTone = when (outcome) {
    "received" -> DotTone.Success
    "pledged" -> DotTone.Primary
    "declined" -> DotTone.Neutral
    else -> DotTone.Neutral
}

/** No stored fiscal-year setting exists yet (Org Settings, out of scope) — approximated from the current calendar year. */
private fun currentFiscalYearLabel(): String = "FY ${kotlin.js.Date().getFullYear()}"

private external interface CompletedPaginationProps : Props {
    var currentPage: Int
    var totalPages: Int
    var onPage: (Int) -> Unit
}

private val CompletedPagination = FC<CompletedPaginationProps> { props ->
    if (props.totalPages > 1) {
        div {
            className = ClassName("drives-pagination")
            CompletedPageButton("←", props.currentPage > 1) { props.onPage(props.currentPage - 1) }
            paginationWindow(props.currentPage, props.totalPages).forEachIndexed { index, page ->
                if (page == null) {
                    div {
                        key = "ellipsis-$index".unsafeCast<Key>()
                        className = ClassName("drives-ellipsis")
                        +"…"
                    }
                } else {
                    CompletedPageButton(page.toString(), true, page == props.currentPage) { props.onPage(page) }
                }
            }
            CompletedPageButton("→", props.currentPage < props.totalPages) { props.onPage(props.currentPage + 1) }
        }
    }
}

private fun react.ChildrenBuilder.CompletedPageButton(
    text: String,
    enabled: Boolean,
    active: Boolean = false,
    onClick: () -> Unit,
) {
    button {
        type = ButtonType.buttonType
        className = ClassName(
            "drives-page-button" + if (active) " drives-page-button-active" else "",
        )
        disabled = !enabled
        ariaLabel = when (text) {
            "←" -> "Previous page"
            "→" -> "Next page"
            else -> "Page $text"
        }
        this.onClick = { onClick() }
        +text
    }
}
